import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Select, Input, message } from 'antd';
import { aiLookupService, type PartWarranty } from '../../services/aiLookupService';

export interface WarrantyEditorModalProps {
  open: boolean;
  optionId: number | null;
  initialWarranty: PartWarranty | null;
  onClose: () => void;
  onSaved: (warranty: PartWarranty | null) => void;
}

const TYPE_OPTIONS = [
  { value: 'limited', label: 'Limited' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'unlimited', label: 'Unlimited' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'none', label: 'None (no warranty)' },
  { value: 'unknown', label: 'Unknown' },
];

export const WarrantyEditorModal: React.FC<WarrantyEditorModalProps> = ({
  open, optionId, initialWarranty, onClose, onSaved,
}) => {
  const [form] = Form.useForm<PartWarranty>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset first to clear any stale fields from a previous part option
    // (setFieldsValue is a merge — without resetFields, notes from option A
    // would persist when the modal reopens for option B).
    form.resetFields();
    if (initialWarranty) {
      form.setFieldsValue(initialWarranty);
    }
  }, [open, initialWarranty, form]);

  const handleSave = async () => {
    if (!optionId) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      const payload: PartWarranty = {
        months: values.months ?? null,
        miles: values.miles ?? null,
        type: values.type,
        notes: values.notes,
      };
      const { warranty } = await aiLookupService.updateWarranty(optionId, payload);
      onSaved(warranty);
      onClose();
    } catch (e: any) {
      // Antd validateFields throws a ValidateErrorEntity (has errorFields) — inline
      // form errors are sufficient. Don't surface a misleading "save failed" toast.
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error ?? e?.message ?? 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!optionId) return;
    setSaving(true);
    try {
      await aiLookupService.updateWarranty(optionId, null);
      onSaved(null);
      onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? e?.message ?? 'clear failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit warranty"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      confirmLoading={saving}
      width={420}
    >
      <Form form={form} layout="vertical" initialValues={{ type: 'unknown' }}>
        <Form.Item label="Months" name="months">
          <InputNumber min={0} max={600} style={{ width: '100%' }} placeholder="e.g. 12" />
        </Form.Item>
        <Form.Item label="Miles" name="miles">
          <InputNumber min={0} max={1000000} style={{ width: '100%' }} placeholder="e.g. 12000" />
        </Form.Item>
        <Form.Item label="Type" name="type" rules={[{ required: true }]}>
          <Select options={TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item label="Notes (optional)" name="notes">
          <Input.TextArea maxLength={500} rows={2} />
        </Form.Item>
        <div style={{ textAlign: 'right' }}>
          <a onClick={handleClear} role="button">Clear warranty</a>
        </div>
      </Form>
    </Modal>
  );
};
